/* -----------------Third parties--------------- */
import { Redirect, Stack, useLocalSearchParams, useRouter } from "expo-router";

import { useUser } from "@clerk/clerk-expo";
import { events } from "@constants/tracking";
import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import VerificationLinkExpired from "@features/auth/components/VerificationLinkExpired";
import { useMagicLinkTokens } from "@features/auth/hooks/useMagicLinkTokens";
import { buildType } from "@helpers/expo-extras";
import { isWeb } from "@helpers/platform";
import { captureErrorToSentry } from "@helpers/sentry-logger";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import useClerkUser from "@hooks/useClerkUser";
import { useDevice } from "@hooks/useDevice";
import { useGlobalStore } from "@hooks/useGlobalStore";

import ContactSupport from "@features/auth/components/ContactSupport";
import OnboardingAppBar from "@features/auth/components/OnboardingAppBar";
import { useOnboardingStore } from "@features/onboardings/hooks/useOnboardingStore";
import useClerkAuth from "@hooks/useClerkAuth";
import { formatError } from "@hooks/useClerkAuth/helpers";
import { isSignUpError } from "@hooks/useClerkAuth/helpers";
import { baseDevUrl, isDev } from "@memorang/constants/src/urls";
import { composeMessage } from "@memorang/helpers/src/email";
import Box from "@memorang/ui/src/components/Box";
import Button from "@memorang/ui/src/components/Button";
import ScrollContainer from "@memorang/ui/src/components/ScrollContainer";
import { useToast } from "@memorang/ui/src/components/Toast";
import { useQuery } from "@tanstack/react-query";
/* -----------------Analytics--------------- */
import { trackCustomEvent } from "analytics";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import DocumentTitle from "components/DocumentTitle";
import * as Device from "expo-device";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Platform } from "react-native";
import { openInbox } from "react-native-email-link";
import { Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const RESEND_EMAIL_TIMEOUT = 90;

const ResendButton = ({
	initialResentEmailIn,
	resendMagicLink,
	disabled,
	isAuthInProgress,
}: {
	initialResentEmailIn: number;
	disabled?: boolean;
	isAuthInProgress?: boolean;
	resendMagicLink: () => void;
}) => {
	const [resentEmailIn, setResentEmailIn] = useState(initialResentEmailIn);
	const disabledResend = disabled || resentEmailIn > 0 || isAuthInProgress;

	const theme = useAppTheme();
	const { t } = useTranslation();
	useEffect(() => {
		if (initialResentEmailIn) {
			setResentEmailIn(initialResentEmailIn);
		}
	}, [initialResentEmailIn]);

	useEffect(() => {
		let timeout: ReturnType<typeof setTimeout>;
		if (resentEmailIn > 0) {
			timeout = setTimeout(() => {
				setResentEmailIn(resentEmailIn - 1);
			}, 1000);
		}
		return () => {
			if (timeout) {
				clearTimeout(timeout);
			}
		};
	}, [resentEmailIn]);
	return (
		<Button
			mode="text"
			onPress={resendMagicLink}
			style={{ width: "100%" }}
			disabled={disabledResend}
			pointerEvents={disabledResend ? "none" : "auto"}
		>
			<Text
				variant="bodyMedium"
				style={{
					color: disabledResend
						? theme.palette.text.disabled
						: theme.colors.primary,
				}}
			>
				{resentEmailIn > 0
					? t("auth.verify.resendMagicLinkIn", {
							time: formatTime(resentEmailIn),
						})
					: t("auth.verify.resendMagicLink")}
			</Text>
		</Button>
	);
};

const VerifyAppbar = memo(
	({
		isLinkExpired,
		handleNavigation,
	}: {
		isLinkExpired: boolean;
		handleNavigation: (back?: boolean) => void;
	}) => {
		const { t } = useTranslation();
		const { isMobile } = useDevice();
		return (
			<OnboardingAppBar
				title={isLinkExpired ? undefined : t("auth.verify.verifyAccountTitle")}
				showLogo={!isMobile}
				onBackAction={() => handleNavigation(true)}
			/>
		);
	},
	(prevProps, nextProps) => {
		return prevProps.isLinkExpired === nextProps.isLinkExpired;
	},
);

const formatTime = (seconds: number) => {
	const minutes = Math.floor(seconds / 60);
	const remainingSeconds = seconds % 60;
	return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
const VerifyRoute = () => {
	const router = useRouter();
	const params = useLocalSearchParams<{ email: string }>();

	const theme = useAppTheme();
	const app = useAppStore((state) => state.app);
	const tenant = useAppStore((state) => state.tenant);
	const baseUrl = useAppStore((state) => state.app.links.baseUrl);
	const [initialResentEmailIn, setInitialResentEmailIn] =
		useState(RESEND_EMAIL_TIMEOUT);
	const appName = app.name;
	const insets = useSafeAreaInsets();
	const { user } = useUser();

	const { showToast } = useToast();
	const onboardingSteps = useAppStore((state) => state.onboarding) || [];

	const redirectUrl = `${isDev ? baseDevUrl : baseUrl}/verify`;
	const { authenticate, isAuthInProgress } = useClerkAuth({
		appId: app.id,
		tenantId: tenant.id,
		redirectUrl,
		onEmailLinkVerify: (_) => {
			setInitialResentEmailIn(RESEND_EMAIL_TIMEOUT);
			showToast({
				message: t("auth.verify.emailResent", { email: emailFromParams }),
			});
		},
	});

	const emailFromParams = params.email
		? params.email.replace(/\s/g, "+")
		: undefined;

	const { t } = useTranslation();

	const supportEmail = useAppStore((state) => state.supportEmail);
	const { showOnboarding } = useOnboardingStore();
	const currentAppVersion = useAppStore((state) => state.app.version);

	const { isSignedIn, userLogicalId } = useClerkUser();
	const setLastLoggedInEmail = useGlobalStore(
		(state) => state.setLastLoggedInEmail,
	);

	const { deepLinkUrl, authMode, os, status, redeemCode } =
		useMagicLinkTokens();
	const isLinkExpired = status === "expired";

	useEffect(() => {
		if (isSignedIn && userLogicalId) {
			const isSignUp = authMode === "signup";
			trackCustomEvent({
				eventName: isSignUp ? events.userRegistered : events.userLoggedIn,
				userId: userLogicalId,
				email: emailFromParams,
				tenantId: tenant.id,
			});
		}
	}, [isSignedIn, userLogicalId, emailFromParams, tenant.id, authMode]);
	useQuery({
		queryKey: ["verify-email", emailFromParams],
		queryFn: async () => {
			const newUser = await user?.reload();
			return newUser ?? null;
		},
		enabled: !userLogicalId,
		refetchInterval: 500,
	});

	const deviceInfo = `- Device: ${Device.deviceName || Device.DeviceType[Device.deviceType || 0]}
		- Operating system: ${Platform.OS}
		- App version: ${currentAppVersion}
		- Build type: ${buildType}
		- Email: ${emailFromParams}`;

	const handleExpiredLinkSupport = () => {
		composeMessage(
			`${app.name} Magic link expired`,
			`I'm getting a verification link that is expired. My device info is as follows:
				${deviceInfo}`,
			supportEmail,
		);
	};

	const handleComposeMessage = () => {
		const subject = `${app.name} Magic link not received`;
		const emailBody = `Hi there, I am currently having trouble finding my magic link to login to the ${app.name} app. Thank you! My device info is as follows:
		${deviceInfo}
		`;
		composeMessage(subject, emailBody, supportEmail);
	};

	const { isMobile } = useDevice();

	useEffect(() => {
		if (emailFromParams) {
			setLastLoggedInEmail(emailFromParams);
		}
	}, [emailFromParams, setLastLoggedInEmail]);

	const handleNavigation = useCallback(
		(back?: boolean) => {
			if (back) {
				router.back();
			}
		},
		[router],
	);

	const handleOpenDeepLink = useCallback(() => {
		if (deepLinkUrl) {
			window.location.href = deepLinkUrl;
		}
	}, [deepLinkUrl]);

	useEffect(() => {
		if (isWeb && isMobile && (os === "android" || os === "ios")) {
			handleOpenDeepLink();
		}
	}, [handleOpenDeepLink, isMobile, os]);

	const cta = emailFromParams
		? t("auth.verify.openEmail")
		: authMode === "signup"
			? t("auth.verify.signUpCta")
			: t("auth.verify.signInCta");
	const loading = isSignedIn && !userLogicalId ? true : !emailFromParams;
	const disabled = isSignedIn && !userLogicalId ? true : !emailFromParams;

	const resendMagicLink = async () => {
		try {
			if (!emailFromParams) {
				throw new Error("Email is required");
			}
			await authenticate(emailFromParams);
		} catch (error: unknown) {
			const isSignUp = isSignUpError(error);
			const longMessage = formatError(error);
			const errorObj = new Error(
				`Error during resend ${isSignUp ? "sign-up" : "sign-in"} magic link`,
			);
			captureErrorToSentry(errorObj, {
				error: JSON.stringify(longMessage),
			});
			trackCustomEvent({
				eventName: isSignUp ? "mem_signup_attempted" : "mem_login_attempted",
				email: emailFromParams,
			});
		}
	};

	if (isSignedIn && userLogicalId) {
		if (authMode === "signup" || showOnboarding) {
			const path = redeemCode
				? (`/redeem?redeemCode=${redeemCode}` as const)
				: onboardingSteps.length > 0
					? ("/onboarding" as const)
					: ("/home" as const);
			return <Redirect href={path} />;
		}

		const path = redeemCode
			? (`/redeem?redeemCode=${redeemCode}` as const)
			: ("/home" as const);
		return <Redirect href={path} />;
	}

	return (
		<>
			<Stack.Screen
				options={{
					headerShown: true,
					header: () => (
						<VerifyAppbar
							isLinkExpired={isLinkExpired}
							handleNavigation={handleNavigation}
						/>
					),
				}}
			/>
			<DocumentTitle
				title={
					isLinkExpired
						? t("auth.verify.verificationLinkExpired")
						: t("auth.verify.verifyAccountTitle")
				}
			/>
			<ScrollContainer maxWidth="xs">
				{isLinkExpired ? (
					<VerificationLinkExpired
						onSupportContact={handleExpiredLinkSupport}
						onLoginRetry={() => router.replace("/")}
					/>
				) : (
					<Box flexGrow={1} height="100%" padding={16}>
						{isWeb && (os === "android" || os === "ios") ? (
							<Box
								flex={1}
								alignItems="center"
								justifyContent="center"
								gap={16}
							>
								<Icon
									name="open-in-app"
									size={120}
									color={theme.colors.primary}
								/>
								<Text variant="titleLarge" style={{ textAlign: "center" }}>
									{t("auth.verify.redirectingToApp", { appName })}
								</Text>

								<Text style={{ textAlign: "center" }}>
									{t("auth.verify.appNotOpen")}
								</Text>
								<Button
									mode="contained"
									onPress={handleOpenDeepLink}
									style={{ width: "100%" }}
								>
									{t("auth.verify.openApp", { appName })}
								</Button>
								<Text style={{ textAlign: "center" }} variant="bodySmall">
									{t("auth.verify.installAppPrompt", { appName })}
								</Text>
							</Box>
						) : (
							<Box gap={16} justifyContent="space-between" flex={1}>
								<Box flex={1} justifyContent="center">
									<Box alignItems="center" justifyContent="center" gap={16}>
										<Icon
											name="email-check-outline"
											size={120}
											color={theme.colors.primary}
										/>

										<Text variant="titleLarge" style={{ textAlign: "center" }}>
											{t("auth.verify.confirmEmail")}
										</Text>

										<Box>
											<CustomMarkdownRenderer
												textAlign="center"
												fontSize={16}
												variant="stem"
												customStyles={{
													text: {
														lineHeight: 24,
													},
												}}
												text={
													deepLinkUrl
														? t(
																authMode === "signup"
																	? "auth.verify.signUpBody"
																	: "auth.verify.signInBody",
																{
																	appName,
																},
															)
														: t("auth.verify.magicLinkEmail", {
																email: emailFromParams,
																appName,
															})
												}
											/>
											<Text
												variant="bodyLarge"
												style={{
													textAlign: "center",
													textDecorationLine: "underline",
												}}
											>
												{t("auth.verify.magicLinkEmailExpiry")}
											</Text>
										</Box>
									</Box>
								</Box>

								<Box
									width="100%"
									gap={16}
									paddingBottom={insets.bottom}
									maxWidth={500}
									marginTop={24}
									alignSelf="center"
								>
									{(!isWeb || authMode) && (
										<Button
											mode="contained"
											onPress={() => openInbox()}
											disabled={disabled}
											loading={loading}
											style={{ width: "100%" }}
										>
											{cta}
										</Button>
									)}
									{!deepLinkUrl && (
										<>
											<ResendButton
												initialResentEmailIn={initialResentEmailIn}
												resendMagicLink={resendMagicLink}
												disabled={disabled}
												isAuthInProgress={isAuthInProgress}
											/>
											<ContactSupport
												label="Need help?"
												disabled={loading}
												email={emailFromParams ?? ""}
												buttonText="Contact support"
												handleOpenSupport={handleComposeMessage}
											/>
										</>
									)}
								</Box>
							</Box>
						)}
					</Box>
				)}
			</ScrollContainer>
		</>
	);
};

export default VerifyRoute;
